/**
 * PillInput - Pill-shaped input field with icon for auth screens
 * Matches the auth-screens-preview design: borderRadius 26, emoji icon, optional password toggle
 */

import React, { useState } from 'react';
import { TextInput, View, TouchableOpacity, StyleSheet, Platform, TextInputProps } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { colors } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface PillInputProps extends Omit<TextInputProps, 'style'> {
  label: string;
  icon: string;
  isPassword?: boolean;
  isInvalid?: boolean;
  errorText?: string;
  /** testID for the inline error text (E2E asserts validation errors) */
  errorTestID?: string;
  /** Override for E2E mode to show password text */
  forceShowPassword?: boolean;
}

const BASE_HEIGHT = 52;
const BASE_LABEL_SIZE = 12;
const BASE_INPUT_SIZE = 15;
const BASE_ICON_SIZE = 18;

export function PillInput({
  label,
  icon,
  isPassword = false,
  isInvalid = false,
  errorText,
  errorTestID,
  forceShowPassword = false,
  ...textInputProps
}: PillInputProps) {
  const { theme: t, fontScale, fontFamily } = useDisplayTheme();
  const [showPassword, setShowPassword] = useState(forceShowPassword);

  const borderColor = isInvalid ? colors.error : t.border;
  const scaledHeight = Math.round(BASE_HEIGHT * fontScale);
  const fontFamilyStyle = fontFamily ? { fontFamily } : undefined;

  return (
    <View style={styles.container}>
      <Text
        allowFontScaling={false}
        style={{
          fontSize: BASE_LABEL_SIZE * fontScale,
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 8,
          color: t.textSecondary,
          ...fontFamilyStyle,
        }}
      >
        {label}
      </Text>
      <View
        style={[
          styles.inputWrapper,
          {
            backgroundColor: t.inputBg,
            borderColor,
            minHeight: scaledHeight,
            borderRadius: scaledHeight / 2,
          },
        ]}
      >
        <Text
          allowFontScaling={false}
          style={{ fontSize: BASE_ICON_SIZE * fontScale, width: Math.round(28 * fontScale) }}
        >
          {icon}
        </Text>
        <TextInput
          allowFontScaling={false}
          style={[
            styles.input,
            {
              color: t.text,
              fontSize: BASE_INPUT_SIZE * fontScale,
              minHeight: scaledHeight,
              ...fontFamilyStyle,
            },
          ]}
          placeholderTextColor={t.inputPlaceholder}
          secureTextEntry={isPassword && !showPassword}
          {...textInputProps}
        />
        {isPassword && (
          <TouchableOpacity
            testID="input-button-toggle-password"
            accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
            accessibilityRole="button"
            style={styles.toggle}
            onPress={() => setShowPassword(!showPassword)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: BASE_ICON_SIZE * fontScale, color: t.inputPlaceholder }}
            >
              {showPassword ? '\u{1F441}\u{FE0F}\u{200D}\u{1F5E8}\u{FE0F}' : '\u{1F441}\u{FE0F}'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      {isInvalid && errorText && (
        <Text
          testID={errorTestID}
          allowFontScaling={false}
          style={{ color: colors.error, marginTop: 6, fontSize: BASE_LABEL_SIZE * fontScale }}
        >
          {errorText}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    paddingHorizontal: 16,
  },
  input: {
    flex: 1,
    paddingVertical: 0,
    ...Platform.select({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- web-only CSS property not in RN types
      web: { outlineStyle: 'none' } as any,
    }),
  },
  toggle: {
    padding: 4,
  },
});
